'use client';

import { EventsCarouselSkeleton } from '@v2/components/EventsCarouselSkeleton/EventsCarouselSkeleton';
import { getEventsByLocation } from '@v2/services/get-events-by-location';
import { getCityLatLonByName } from '@v2/services/getCityLatLonByName';
import { EventCardDTO } from '@v2/types';
import { LeagueCarouselEvents } from '@v2/views/events/components/LeagueCarouselEvents/LeagueCarouselEvents';
import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

type cords = {
  latitude?: number;
  longitude?: number;
};

interface Props {
  location?: string;
  initialEvents: EventCardDTO[];
  isLocationLoading?: boolean;
  itemListName?: string;
}

export const EventsCarousel = React.memo(
  ({
    location,
    initialEvents,
    isLocationLoading = false,
    itemListName = 'nearby_events',
  }: Props) => {
    const [events, setEvents] = useState<EventCardDTO[]>();
    const [isLoading, setIsLoading] = useState(false);

    const pathname = usePathname();

    const isConcertsPage = pathname === '/concerts';

    useEffect(() => {
      let isActive = true;

      const getEvents = async () => {
        setIsLoading(true);

        try {
          const cityData = await getCityLatLonByName(location || '');

          if (!cityData || !isActive) return;

          const response = await getEventsByLocation({
            serverSide: false,
            lat: cityData?.Geometry.lat,
            lon: cityData?.Geometry.lon,
            perPage: 12,
            concerts: isConcertsPage,
            withVenueImages: true,
            sports: !isConcertsPage,
          });

          const { mapEventCardDTOs } =
            await import('@v2/views/events/util/mapEventCardDTO');
          if (isActive) setEvents(mapEventCardDTOs(response.events, 12));
        } catch (e) {
          if (isActive) console.error(e);
        } finally {
          if (isActive) setIsLoading(false);
        }
      };
      if (location) {
        getEvents();
      }

      return () => {
        isActive = false;
      };
    }, [isConcertsPage, location]);

    return (
      <>
        {isLoading || (isLocationLoading && !initialEvents?.length) ? (
          <EventsCarouselSkeleton withTitle={false} />
        ) : (
          <LeagueCarouselEvents
            events={events || initialEvents}
            itemListName={itemListName}
            itemCategory={isConcertsPage ? 'concerts' : 'sports'}
          />
        )}
      </>
    );
  }
);
